// The company's setup after Getting started (settings prototype): the price book, people's
// profiles, departments and job types, tax zones, message templates and stock locations. Each form
// is plain text; these turn it into the action's input, sending only what changed, or say the one
// thing to fix first.

import { normalizePhone } from '@flowology/kernel';
import { pictureShownFirst, type ArtTheme, type ItemPictures, type PictureFirst } from '@flowology/domain';
import { dollarsToCents, centsToDollars } from './settings-model.ts';
import type { ItemMediaEntry, ItemPictureProps, ItemTerms, ItemUse } from '@flowology/ui-web';

export type Built = { input: Record<string, unknown> } | { error: string };
export const NOTHING = 'Nothing has changed yet.';

/**
 * What the person may change here, by the permissions they hold (packages/auth permissions.ts),
 * whichever role gives them, a company's own included (QA-681); the server checks again.
 */
export function setupRights(held: readonly string[]) {
  const has = (p: string) => held.includes(p);
  return {
    /** pricebook.edit */
    pricebook: has('pricebook.edit'),
    /** org.edit: departments, job types, tax zones */
    org: has('org.edit'),
    /** settings.edit: the wording of customer messages */
    templates: has('settings.edit'),
    /** staff.edit */
    staff: has('staff.edit'),
    /** inventory.manage */
    stock: has('inventory.manage'),
    /** marketing.manage: changes campaigns and tracking numbers (BL-008) */
    marketing: has('marketing.manage'),
    /** marketing.view: sees them (Ads and tracking numbers) */
    seesMarketing: has('marketing.view'),
  };
}

const changes = (pairs: [string, unknown, unknown][]) => {
  const out: Record<string, unknown> = {};
  for (const [k, now, was] of pairs) if (JSON.stringify(now) !== JSON.stringify(was)) out[k] = now;
  return out;
};
const cents = (text: string, what: string): number | null | { error: string } => {
  if (!text.trim()) return null;
  const c = dollarsToCents(text);
  return c === null ? { error: `The ${what} doesn’t look like an amount. Type it like 129.00.` } : c;
};
const isErr = (x: unknown): x is { error: string } => typeof x === 'object' && x !== null && 'error' in x;
/** Skills as typed ("gas, backflow") ↔ the list the server keeps. */
export const skillsText = (s: readonly string[]) => s.join(', ');
export const skillsList = (t: string) =>
  [
    ...new Set(
      t
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean),
    ),
  ].slice(0, 20);

// ── the price book ──

export const TAX_CLASSES = [
  ['standard', 'Standard'],
  ['labour', 'Labour'],
  ['materials', 'Materials'],
] as const;
export const ITEM_TYPES = [
  ['service', 'Service'],
  ['material', 'Material'],
  ['equipment', 'Equipment'],
] as const;

export interface Item {
  id: string;
  categoryId: string | null;
  type: string;
  code: string;
  name: string;
  description: string | null;
  unit: string;
  priceCents: number;
  memberPriceCents: number | null;
  /** Only for the owner and those they granted it (pricebook.cost.view); absent otherwise. */
  costCents?: number | null;
  taxable: boolean;
  taxClass: string;
  spiffCents: number;
  active: boolean;
  rowVersion: number;
  /** ServiceTitan's item: read-only while the company mirrors it (BL-096). */
  fromServiceTitan?: boolean;
  /** A listed row's picture (pricebook.listItems): its first picture's thumbnail, or null. */
  picture?: ItemPictureProps['picture'];
  /** Its house-style drawing, light and dark, passed ones only (pricebook.listItems and getItem, BL-109). */
  art?: ItemPictures['art'];
  /** The item's pictures and spec sheets, in order (pricebook.getItem). */
  media?: ItemMediaEntry[];
  /** What a service uses (pricebook.getItem, price book package 1): read only. */
  materials?: ItemUse[];
  /** Its sold hours, add-on price and warranty, when it has any. */
  details?: ItemTerms | null;
}

/**
 * A row's picture (BL-109, Amir 25 Oct): by the company's pricebook.art.showFirst, its passed drawing in
 * the viewer's light or dark, else its primary photo, else none; or its imported photo first (domain's
 * pictureShownFirst, the one rule for the picture shown first).
 */
export function rowPicture(
  i: Pick<Item, 'art' | 'picture'>,
  theme: ArtTheme,
  first: PictureFirst = 'drawing',
): ItemPictureProps['picture'] {
  const p = pictureShownFirst(i, theme, first);
  return p ? { fileId: p.id, thumbUrl: p.thumbUrl } : null;
}

/** Whether the item has a drawing that passed. */
export const hasDrawing = (i: Pick<Item, 'art'>) => !!(i.art?.light ?? i.art?.dark);

/** Whether the item sheet puts its photos above its drawing: only when the company shows imported first. */
export function photosFirst(i: Pick<Item, 'art' | 'media'>, theme: ArtTheme, first: PictureFirst): boolean {
  if (first !== 'imported') return false;
  const photos = (i.media ?? []).filter((m) => m.kind === 'photo');
  const lead = photos.find((m) => m.isPrimary) ?? photos[0];
  const picture = lead ? { fileId: lead.fileId, thumbUrl: lead.thumbUrl, imported: lead.imported } : null;
  return pictureShownFirst({ art: i.art, picture }, theme, first)?.kind === 'photo';
}

/**
 * The systems a price book item can be imported from, by the flag the price book sends for each, and the
 * name people know it by. A new import source is one line here; no screen names a system itself.
 */
const IMPORT_SOURCES: readonly (readonly [keyof Pick<Item, 'fromServiceTitan'>, string])[] = [
  ['fromServiceTitan', 'ServiceTitan'],
];

/** The system the item was imported from, by name, or null when it was made here. */
export function importedFrom(i: Partial<Pick<Item, 'fromServiceTitan'>>): { source: string } | null {
  const hit = IMPORT_SOURCES.find(([flag]) => i[flag] === true);
  return hit ? { source: hit[1] } : null;
}

/**
 * The words above an item's pictures when some were imported, kept for reference: "Imported from
 * <system>" when the item says which, else "Imported photo"; null when a person added them all here.
 */
export function importedWords(i: Pick<Item, 'media' | 'fromServiceTitan'>): string | null {
  const kept = (i.media ?? []).filter((m) => m.imported === true);
  if (!kept.length) return null;
  const from = importedFrom(i);
  if (from) return `Imported from ${from.source}, kept for reference.`;
  return kept.filter((m) => m.kind === 'photo').length > 1
    ? 'Imported photos, kept for reference.'
    : 'Imported photo, kept for reference.';
}

/** pricebook.setMedia's input to show this picture first: the same pictures, in the same order. */
export function showFirstInput(i: Pick<Item, 'id' | 'rowVersion' | 'media'>, fileId: string): Built {
  const media = i.media ?? [];
  if (!media.some((m) => m.fileId === fileId && m.kind === 'photo'))
    return { error: 'That picture isn’t on this item any more. Close it and open it again.' };
  return {
    input: {
      itemId: i.id,
      rowVersion: i.rowVersion,
      media: media.map((m) => ({ fileId: m.fileId, caption: m.caption })),
      primaryFileId: fileId,
    },
  };
}

/**
 * Whether this item can be changed here: by who edits the price book, and while the company mirrors
 * ServiceTitan only Flowology's own items (Brook's parts), never ServiceTitan's (BL-096).
 */
export const itemEditable = (i: Pick<Item, 'fromServiceTitan'>, canEdit: boolean, mirroring: boolean) =>
  canEdit && !(mirroring && i.fromServiceTitan === true);
export interface ItemForm {
  name: string;
  description: string;
  unit: string;
  price: string;
  memberPrice: string;
  cost: string;
  taxable: boolean;
  taxClass: string;
  categoryId: string;
  active: boolean;
}
export const itemForm = (i: Item): ItemForm => ({
  name: i.name,
  description: i.description ?? '',
  unit: i.unit,
  price: centsToDollars(i.priceCents),
  memberPrice: i.memberPriceCents === null ? '' : centsToDollars(i.memberPriceCents),
  cost: i.costCents === undefined || i.costCents === null ? '' : centsToDollars(i.costCents),
  taxable: i.taxable,
  taxClass: i.taxClass,
  categoryId: i.categoryId ?? '',
  active: i.active,
});
export function itemInput(i: Item, f: ItemForm): Built {
  const name = f.name.trim();
  if (!name) return { error: 'Give it a name.' };
  if (!f.unit.trim()) return { error: 'Say what it’s sold by, like each or hour.' };
  const price = dollarsToCents(f.price);
  if (price === null) return { error: 'The price doesn’t look like an amount. Type it like 129.00.' };
  const member = cents(f.memberPrice, 'member price');
  if (isErr(member)) return member;
  const cost = i.costCents === undefined ? undefined : cents(f.cost, 'cost');
  if (isErr(cost)) return cost;
  const changed = changes([
    ['name', name, i.name],
    ['description', f.description.trim() || null, i.description],
    ['unit', f.unit.trim(), i.unit],
    ['priceCents', price, i.priceCents],
    ['memberPriceCents', member, i.memberPriceCents],
    ...(cost === undefined
      ? []
      : ([['costCents', cost, i.costCents ?? null]] as [string, unknown, unknown][])),
    ['taxable', f.taxable, i.taxable],
    ['taxClass', f.taxClass, i.taxClass],
    ['categoryId', f.categoryId || null, i.categoryId],
    ['active', f.active, i.active],
  ]);
  if (!Object.keys(changed).length) return { error: NOTHING };
  return { input: { id: i.id, rowVersion: i.rowVersion, ...changed } };
}
export interface NewItemForm {
  type: string;
  code: string;
  name: string;
  unit: string;
  price: string;
  memberPrice: string;
  taxable: boolean;
  categoryId: string;
}
export const emptyItemForm = (): NewItemForm => ({
  type: 'service',
  code: '',
  name: '',
  unit: 'each',
  price: '',
  memberPrice: '',
  taxable: true,
  categoryId: '',
});
/** A code from the name, as Getting started makes one ("Drain cleaning" → DRAIN-CLEANING). */
export const codeFrom = (name: string) =>
  name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
export function newItemInput(f: NewItemForm): Built {
  const name = f.name.trim();
  if (!name) return { error: 'Give it a name.' };
  const code = f.code.trim() || codeFrom(name);
  if (!/^[A-Za-z0-9._-]{1,40}$/.test(code)) return { error: 'The code is letters, digits, dots and dashes.' };
  const price = dollarsToCents(f.price);
  if (price === null) return { error: 'The price doesn’t look like an amount. Type it like 129.00.' };
  const member = cents(f.memberPrice, 'member price');
  if (isErr(member)) return member;
  return {
    input: {
      type: f.type,
      code,
      name,
      unit: f.unit.trim() || 'each',
      priceCents: price,
      ...(member !== null ? { memberPriceCents: member } : {}),
      taxable: f.taxable,
      ...(f.categoryId ? { categoryId: f.categoryId } : {}),
    },
  };
}

// ── a person's profile (staff.updateProfile) ──

export interface Staff {
  id: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  color: string | null;
  bio: string | null;
  skills: string[];
  isTechnician: boolean;
  status: 'active' | 'inactive';
  rowVersion: number;
  /** Their first name, as messages greet them ("Dana"). */
  firstName?: string;
  /** The profile photo (staff.addPhoto); its link only once the bucket holds it, checked. */
  photoFileId: string | null;
  photoUrl: string | null;
}
export interface ProfileForm {
  displayName: string;
  email: string;
  phone: string;
  color: string;
  bio: string;
  skills: string;
  isTechnician: boolean;
  active: boolean;
}
export const profileForm = (s: Staff): ProfileForm => ({
  displayName: s.displayName,
  email: s.email ?? '',
  phone: s.phone ?? '',
  color: s.color ?? '',
  bio: s.bio ?? '',
  skills: skillsText(s.skills),
  isTechnician: s.isTechnician,
  active: s.status === 'active',
});
export function profileInput(s: Staff, f: ProfileForm): Built {
  const name = f.displayName.trim();
  if (!name) return { error: 'Add their name.' };
  const phone = f.phone.trim() ? normalizePhone(f.phone) : null;
  if (phone === null && f.phone.trim())
    return { error: 'That phone number doesn’t look right. Type it with its area code, like 604-555-0198.' };
  const color = f.color.trim() || null;
  if (color && !/^#[0-9A-Fa-f]{6}$/.test(color)) return { error: 'Pick a colour.' };
  const changed = changes([
    ['displayName', name, s.displayName],
    ['email', f.email.trim() || null, s.email],
    ['phone', phone, s.phone],
    ['color', color, s.color],
    ['bio', f.bio.trim() || null, s.bio],
    ['skills', skillsList(f.skills), s.skills],
    ['isTechnician', f.isTechnician, s.isTechnician],
    ['status', f.active ? 'active' : 'inactive', s.status],
  ]);
  if (!Object.keys(changed).length) return { error: NOTHING };
  return { input: { id: s.id, rowVersion: s.rowVersion, ...changed } };
}

/**
 * QA-722: what making someone inactive does, said before it's saved: "Dana has 3 booked visits;
 * they'll need another technician." Their visits stay on the board, waiting for one. `booked` is
 * null until the count is read (or when it can't be).
 */
export function inactiveWords(s: Pick<Staff, 'displayName' | 'firstName'>, booked: number | null): string {
  const name = s.firstName || s.displayName.split(/\s+/)[0] || s.displayName;
  const visits =
    booked === 1
      ? `${name} has 1 booked visit; it’ll need another technician. `
      : booked
        ? `${name} has ${booked} booked visits; they’ll need another technician. `
        : '';
  return (
    `${visits}They leave the board and can’t be booked; ${booked ? 'their visits wait' : 'any visit they’re on waits'} ` +
    'on the board for another technician. Their sign-in is separate: turn it off under their sign-in.'
  );
}

// ── departments (business units) and job types ──

export interface Unit {
  id: string;
  code: string;
  name: string;
  defaultInvoiceMode: 'flat_rate' | 'time_and_materials';
  estimateRequired: boolean;
  signatureRequired: boolean;
  techCanInvoice: boolean;
  ratingEnabled: boolean;
  poVisible: boolean;
  materialsBillable: boolean;
  active: boolean;
  rowVersion?: number;
}
/** The rules a department sets for its jobs, in the words on the form. */
export const UNIT_RULES = [
  ['estimateRequired', 'An estimate is signed before the work'],
  // "The customer signs to accept the finished work" is the setting invoices.signatureRequired now
  // (BL-052): the sheet writes the department's, Rules & defaults the company's
  ['techCanInvoice', 'Technicians can take payment on site'],
  ['ratingEnabled', 'Customers are asked to rate the visit'],
  ['poVisible', 'Ask for a PO number'],
  ['materialsBillable', 'Materials are billed'],
] as const;
export type UnitForm = Omit<Unit, 'id' | 'rowVersion'>;
export const emptyUnitForm = (): UnitForm => ({
  code: '',
  name: '',
  defaultInvoiceMode: 'flat_rate',
  estimateRequired: false,
  signatureRequired: true,
  techCanInvoice: true,
  ratingEnabled: true,
  poVisible: false,
  materialsBillable: true,
  active: true,
});
export function unitInput(was: Unit | null, f: UnitForm): Built {
  const name = f.name.trim();
  if (!name) return { error: 'Give the department a name.' };
  const fields = ['defaultInvoiceMode', ...UNIT_RULES.map((r) => r[0]), 'active'] as const;
  if (!was) {
    const code = (f.code.trim() || codeFrom(name)).slice(0, 20);
    if (!/^[A-Za-z0-9_-]{1,20}$/.test(code)) return { error: 'The code is letters, digits and dashes.' };
    return { input: { code, name, ...Object.fromEntries(fields.map((k) => [k, f[k]])) } };
  }
  const changed = changes([
    ['name', name, was.name],
    ...fields.map((k) => [k, f[k], was[k]] as [string, unknown, unknown]),
  ]);
  if (!Object.keys(changed).length) return { error: NOTHING };
  return { input: { id: was.id, ...(was.rowVersion ? { rowVersion: was.rowVersion } : {}), ...changed } };
}

/**
 * While the company mirrors ServiceTitan, a department's own defaults alone (businessUnits.setDefaults):
 * its name and whether it's archived are ServiceTitan's then (BL-097).
 */
export function unitDefaultsInput(was: Unit, f: UnitForm): Built {
  const fields = ['defaultInvoiceMode', ...UNIT_RULES.map((r) => r[0])] as const;
  const changed = changes(fields.map((k) => [k, f[k], was[k]] as [string, unknown, unknown]));
  if (!Object.keys(changed).length) return { error: NOTHING };
  return { input: { id: was.id, ...(was.rowVersion ? { rowVersion: was.rowVersion } : {}), ...changed } };
}

export interface JobType {
  id: string;
  businessUnitId: string | null;
  code: string;
  name: string;
  defaultKind: 'estimate_visit' | 'service' | null;
  defaultDurationMinutes: number;
  skills: string[];
  /** BL-027: the licences a visit of this type needs, held by someone on it that day. */
  requiredLicenceKindIds: string[];
  active: boolean;
  rowVersion: number;
}
export interface JobTypeForm {
  code: string;
  name: string;
  businessUnitId: string;
  defaultKind: '' | 'estimate_visit' | 'service';
  duration: string;
  skills: string;
  /** BL-027: the licence kinds ticked. */
  licences: string[];
  active: boolean;
}
export const jobTypeForm = (j: JobType | null): JobTypeForm => ({
  code: j?.code ?? '',
  name: j?.name ?? '',
  businessUnitId: j?.businessUnitId ?? '',
  defaultKind: j?.defaultKind ?? '',
  duration: String(j?.defaultDurationMinutes ?? 120),
  skills: skillsText(j?.skills ?? []),
  licences: j?.requiredLicenceKindIds ?? [],
  active: j?.active ?? true,
});
export function jobTypeInput(was: JobType | null, f: JobTypeForm): Built {
  const name = f.name.trim();
  if (!name) return { error: 'Give the job type a name.' };
  const minutes = Number(f.duration);
  if (!Number.isInteger(minutes) || minutes < 15 || minutes > 1440)
    return { error: 'The usual length is 15 minutes to a day, in minutes.' };
  const values = {
    name,
    businessUnitId: f.businessUnitId || null,
    defaultKind: f.defaultKind || null,
    defaultDurationMinutes: minutes,
    skills: skillsList(f.skills),
    requiredLicenceKindIds: f.licences,
  };
  if (!was) {
    const code = (f.code.trim() || codeFrom(name)).slice(0, 30);
    if (!/^[A-Za-z0-9_-]{1,30}$/.test(code)) return { error: 'The code is letters, digits and dashes.' };
    const { requiredLicenceKindIds, ...rest } = values;
    return {
      input: { code, ...rest, ...(requiredLicenceKindIds.length ? { requiredLicenceKindIds } : {}) },
    };
  }
  const changed = changes([
    ...Object.entries(values).map(([k, v]) => [k, v, was[k as keyof JobType]] as [string, unknown, unknown]),
    ['active', f.active, was.active],
  ]);
  if (!Object.keys(changed).length) return { error: NOTHING };
  return { input: { id: was.id, rowVersion: was.rowVersion, ...changed } };
}

/**
 * While the company mirrors ServiceTitan, a job type's own columns alone (jobTypes.setDefaults): its
 * usual kind and length, skills and licences; its name, department and offering are ServiceTitan's (BL-098).
 */
export function jobTypeDefaultsInput(was: JobType, f: JobTypeForm): Built {
  const minutes = Number(f.duration);
  if (!Number.isInteger(minutes) || minutes < 15 || minutes > 1440)
    return { error: 'The usual length is 15 minutes to a day, in minutes.' };
  const changed = changes([
    ['defaultKind', f.defaultKind || null, was.defaultKind],
    ['defaultDurationMinutes', minutes, was.defaultDurationMinutes],
    ['skills', skillsList(f.skills), was.skills],
    ['requiredLicenceKindIds', f.licences, was.requiredLicenceKindIds],
  ]);
  if (!Object.keys(changed).length) return { error: NOTHING };
  return { input: { id: was.id, rowVersion: was.rowVersion, ...changed } };
}

// ── tax zones ──

export interface Zone {
  id: string;
  name: string;
  /** false: retired in ServiceTitan (#83): it keeps the invoices it taxed, never offered for new work */
  active?: boolean;
  components: {
    id: string;
    code: string;
    rate: string;
    appliesTo: string[];
    effectiveFrom: string;
    effectiveTo: string | null;
  }[];
}
export interface RateForm {
  code: string;
  percent: string;
  appliesTo: string[];
  from: string;
}
export const emptyRateForm = (today: string): RateForm => ({
  code: '',
  percent: '',
  appliesTo: ['standard', 'labour', 'materials'],
  from: today,
});
/** "5" or "7.5" percent ↔ the "0.05" the server keeps. */
export function rateFromPercent(p: string): string | null {
  const t = p.replace(/[%\s]/g, '');
  if (!/^\d{1,2}(\.\d{1,4})?$/.test(t)) return null;
  const r = Number(t) / 100;
  if (r >= 1) return null;
  return String(Number(r.toFixed(6)));
}
export const percentOf = (rate: string) => `${Number((Number(rate) * 100).toFixed(4))}%`;
export function componentInput(f: RateForm): { component: Record<string, unknown> } | { error: string } {
  const code = f.code.trim().toUpperCase();
  if (!/^[A-Z][A-Z0-9]{0,9}$/.test(code)) return { error: 'Name the tax in capitals, like GST or PST.' };
  const rate = rateFromPercent(f.percent);
  if (rate === null) return { error: 'The rate is a percent between 0 and 99, like 5 or 7.5.' };
  if (!f.appliesTo.length) return { error: 'Say what it applies to.' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.from)) return { error: 'Pick the date it starts.' };
  return { component: { code, rate, appliesTo: f.appliesTo, effectiveFrom: f.from } };
}
export function zoneInput(name: string, rates: RateForm[]): Built {
  const n = name.trim();
  if (!n) return { error: 'Name the zone, like British Columbia.' };
  const comps = [];
  for (const r of rates) {
    const c = componentInput(r);
    if ('error' in c) return c;
    comps.push(c.component);
  }
  if (!comps.length) return { error: 'Add at least one tax.' };
  return { input: { name: n, components: comps } };
}
/** The rate in force now for each tax of a zone, and the ones that start later. */
export function ratesNow(z: Zone, today: string) {
  const now = z.components.filter(
    (c) => c.effectiveFrom <= today && (!c.effectiveTo || c.effectiveTo > today),
  );
  const later = z.components.filter((c) => c.effectiveFrom > today);
  return { now, later };
}

// ── message templates ──

export interface Template {
  id: string;
  key: string;
  channel: 'sms' | 'email';
  purpose: string;
  subject: string | null;
  body: string;
  version: number;
  fields: string[];
}
export function templateInput(t: Template, subject: string, body: string): Built {
  const b = body.trim();
  if (!b) return { error: 'The message needs some words.' };
  const s = subject.trim();
  if (t.channel === 'email' && !s) return { error: 'An email needs a subject.' };
  if (b === t.body && (t.channel !== 'email' || s === (t.subject ?? ''))) return { error: NOTHING };
  return { input: { templateId: t.id, body: b, ...(t.channel === 'email' ? { subject: s } : {}) } };
}
/** The {{fields}} a body uses that the template doesn't offer: the server refuses them, so say so first. */
export function unknownFields(t: Template, text: string): string[] {
  const used = [...text.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map((m) => m[1]!);
  return [...new Set(used.filter((f) => !t.fields.includes(f)))];
}

// ── stock locations (a warehouse, or a tech's truck) ──

export function stockInput(name: string, kind: 'warehouse' | 'truck', staffId: string): Built {
  const n = name.trim();
  if (!n) return { error: kind === 'truck' ? 'Name the truck, like Truck 3.' : 'Name the warehouse.' };
  return { input: { name: n, kind, ...(kind === 'truck' && staffId ? { staffId } : {}) } };
}
